import type { Terms } from "./i18n";
import type { TermOverrides } from "./insurer";

/**
 * The guide of the configuration pages as an insurer reads it, where it
 * differs from a law firm's (`guide.ts`): the terms of `insurer.ts`, and what
 * an input means for the reserve. The Costs page keeps its labels "Incurred
 * costs" and "Estimated costs" for an insurer, so the guide names them as
 * labels and says what they mean. The Fee structure page is not an insurer's.
 */
export const insurerGuideTerms: TermOverrides<Terms["guide"]> = {
  caseWide: {
    title: "Assumptions that hold for the whole claim",
  },
  pages: {
    parties: {
      entries: {
        client: {
          title: "The insured and other parties",
          enter: "In the party editor, whether the party is insured with you.",
          assumes:
            "Every party's outcomes are calculated the same way, insured or not. The insured comes first in the analytics, the reserve is worked out for it, and the Agreements page is for the insured.",
        },
      },
      general: {
        effect: {
          text: "Limited assets of the insured limit what it pays, and so the reserve. Limited assets of an opponent lower what can be recovered from it.",
        },
        removal: {
          text: "Removing a party deletes the heads of claim it brought and takes it off those against it. Its share of the other parties' apportionment goes too, with the parts of their costs towards it, so their budgets fall by those amounts.",
        },
      },
    },
    timeline: {
      entries: {
        dates: {
          title: "Dates of the claim",
          enter: "When the claim started, the date of loss and the date notified.",
          assumes:
            "The timeline counts from the start of the claim: the first stage is expected to end its duration after that day, and each stage after it counts from the end of the one before. The dates of loss and notification are kept with the claim.",
        },
        ended: {
          enter:
            "The day the stage ended and, for each insured and cost type, what was actually spent.",
          assumes:
            "Closing moves the claim on to the next stage. Spending less than planned leaves the rest outstanding; spending more raises the budget by the difference, apportioned as the costs so far. Other parties' costs at the stage stay as they are. Reopening the stage closed last takes its end date away and keeps its costs.",
        },
        status: {
          assumes:
            "Closed: the stage has ended. Open: the claim is in this stage. Overdue: it is open past its expected end, and the stages after it keep the dates you planned until you enter a new duration or close it. Not planned: it has no expected end.",
        },
      },
      general: {
        effect: {
          text: "Dates and durations change no figure: they plan the claim. Closing a stage can, through the costs it records. Closing a stage and accepting a settlement each record a snapshot of the reserve.",
        },
        standing: {
          title: "Where the claim stands",
          text: "The open stage is where the claim stands. The reserve history shows it, and the Cost progress slider on the Costs page moves within it.",
        },
      },
    },
    claims: {
      entries: {
        parties: {
          enter: "Who brings the head of claim, and against whom.",
          assumes:
            "A head of claim is decided against each defendant: a defendant-specific issue separately for each of them, a generic issue once for all. The claimant is paid each claimed amount once, however many defendants lose. Costs follow the head of claim against each defendant: whoever wins it recovers costs from the other side, at its rates on the Recoveries page.",
        },
        settled: {
          assumes:
            "Once an offer is accepted, the heads of claim it settles leave the calculation for that defendant. The part of the settlement sum apportioned to a claimed amount is deducted from it for the defendants that remain.",
        },
        chances: {
          example:
            "Against one defendant, issue 1 at 70% and issue 2 at 80%: the head of claim is won against it with 70% × 80% = 56%, and the defence succeeds with 44%.",
        },
        summary: {
          assumes:
            "Under each defendant, the chance of winning the head of claim against it. Against all: against every defendant still in the claim. Against any: against at least one of them. A settled defendant no longer counts.",
        },
        amounts: {
          enter:
            "Each damage with the amount claimed, and one or more estimates of the share a court would award if the head of claim succeeds.",
          assumes:
            "An amount is awarded only if the whole head of claim is won against a defendant liable for it: winning single issues gives nothing. Several award estimates are averaged by their weights into one award, used in every outcome where the head of claim is won. A damage claimed under several heads of claim is awarded once, at the highest amount among those won. No interest is added.",
          example:
            "Award estimates of 90% and 80% with equal weights give one award of 85%: £799,000 of the £940,000 claimed, in every outcome where the head of claim is won.",
        },
      },
      general: {
        effect: {
          text: "The chances weight every outcome of the claim and the amounts decide the indemnity in each: together they drive the reserve, the average cost over the outcomes.",
        },
        type: {
          text: "Groups and filters across the portfolio. It changes no figure of this claim.",
        },
        counterclaims: {
          text: "Enter a counterclaim as a head of claim of its own with the roles reversed. It is decided independently of the head of claim it answers, and its costs follow it separately.",
        },
      },
    },
    costs: {
      entries: {
        editor: {
          assumes:
            "An outcome spends the costs of the stages it reaches: up to Trial, and for a party that settles everything only up to its last settlement. A party spends its Enforcement costs only in an outcome where it collects from an opponent: the part towards that opponent and the generic part. Costs of a stage an outcome does not reach are not spent, paid or not, though a CFA's base counts them (see the Agreements page). Appeals have no costs of their own here.",
        },
        progress: {
          assumes:
            "\"Incurred costs\" on this page are paid; \"Estimated costs\" are outstanding. The slider moves the costs of the open stage alone: the stages before it keep what closing them recorded, and those after it stay outstanding. To go on to the next stage, close this one.",
        },
      },
      general: {
        effect: {
          text: "The insured's costs are the defence costs of the reserve, with any CFA fees and the VAT it pays. What the insured recovers of them is a recovery in the reserve.",
        },
        settlements: {
          text: "Once a settlement ends the claim with an opponent, what was paid towards it at acceptance stays paid, and what was still outstanding towards it is no longer to be spent. The Cost editor shows what settlements fixed beside its inputs, and the slider works on the rest. A party that has settled with all its opponents is out of the claim: all its costs are fixed.",
        },
      },
    },
    agreements: {
      entries: {
        lawFirmCfa: {
          assumes:
            "The fees the agreement covers fall due only once it is known whether a success step is reached. If one is, the insured pays the base costs and a success fee. If none is, it pays the no-win fee, if there is one, and the law firm bears the rest. Reaching a step is not the same as winning: it depends on what the step measures.",
        },
        covered: {
          assumes:
            "While the claim goes on, the base is the party's whole budget of the types covered, less what settlements left not to be spent, plus its settlement costs of those types: work at a stage an outcome never reaches counts too. Once the party has settled everything, the base is what it has paid. A CFA covers law firm fees, and barrister fees where switched on; no other disbursements.",
        },
        lost: {
          title: "If the claim is lost",
        },
      },
    },
    recoveries: {
      entries: {
        awarded: {
          assumes:
            "The party succeeds against an opponent when it wins a head of claim against it, or when that opponent's head of claim against the party fails. One rate covers all its costs of that kind, at every stage, paid and outstanding. A rate left at 0% recovers nothing.",
          example:
            "A party wins its head of claim against its only opponent. Of £88,500 of law firm fees, at 70% the opponent is ordered to pay £61,950, as far as its assets allow. The party bears the other £26,550 itself.",
        },
      },
      general: {
        bothWays: {
          text: "If the party wins one head of claim against an opponent and loses another, each side recovers its own costs from the other, at its own rates. No court weighs the two results against each other.",
        },
      },
    },
    apportionments: {
      entries: {
        shares: {
          enter: "The share of the party's costs spent against each opponent.",
          assumes:
            "Each share is of the party's full budget. An opponent is liable for the costs towards it only when the party wins against it. Moving a share moves paid and outstanding costs at their ratio, so the cost progress stays as it is.",
        },
      },
      general: {
        settled: {
          text: "Settling changes no share: all stay shares of the full budget. Once a settlement ends the claim with an opponent, the costs towards it are locked, and the other shares can grow only into generic costs no settlement covers.",
        },
      },
    },
  },
};

/** The guide in an insurer's own dispute with a policyholder: the client party is the insurer. */
export const policyholderGuideTerms: TermOverrides<Terms["guide"]> = {
  pages: {
    parties: {
      entries: {
        client: {
          title: "You and the policyholder",
          enter: "In the party editor, mark yourself, the insurer.",
          assumes:
            "Every party's outcomes are calculated the same way. You come first in the analytics, the reserve is worked out for you, and the Agreements page is for you.",
        },
      },
      general: {
        effect: {
          text: "Your limited assets would limit what you pay, and so the reserve. Limited assets of the policyholder lower what can be recovered from it.",
        },
      },
    },
    timeline: {
      entries: {
        ended: {
          enter:
            "The day the stage ended and, by cost type, what you actually spent.",
        },
      },
    },
    claims: {
      general: {
        effect: {
          text: "The chances weight every outcome of the claim and the amounts decide the policy benefit in each: together they drive the reserve, the average cost over the outcomes.",
        },
      },
    },
    costs: {
      general: {
        effect: {
          text: "Your costs are the defence costs of the reserve, with any CFA fees and the VAT you pay. What you recover of them is a recovery in the reserve.",
        },
      },
    },
    agreements: {
      entries: {
        lawFirmCfa: {
          assumes:
            "The fees the agreement covers fall due only once it is known whether a success step is reached. If one is, you pay the base costs and a success fee. If none is, you pay the no-win fee, if there is one, and the law firm bears the rest. Reaching a step is not the same as winning: it depends on what the step measures.",
        },
      },
    },
  },
};
